import React, { useState, useEffect } from 'react';
import TimerDisplay from './TimerDisplay';
import TimeInput from './TimeInput';
import {
  isoToHM,
  diffSeconds,
  secToClock,
  minutesToEst,
  minutesToEstDigits,
  estInputToMinutes,
} from './timeUtils';
import { NAME_SHRINK_1, NAME_SHRINK_2, NAME_SHRINK_PC } from './uiConfig';

/**
 * 文字数に応じて、名前の字を小さくする。
 *
 * スマホとパソコンでしきい値も大きさも違うので、両方の印を付けておき、
 * どちらを使うかはCSS（画面の幅）に任せる。
 *   sm / xs  … スマホ用
 *   pc-sm    … パソコン用
 */
const nameSizeClass = (name = '') => {
  const len = [...name].length;
  const cls = [];
  if (len > NAME_SHRINK_2) cls.push('xs');
  else if (len > NAME_SHRINK_1) cls.push('sm');
  if (len > NAME_SHRINK_PC) cls.push('pc-sm');
  return cls.join(' ');
};

function TaskRow({
  task,
  mode, // 'running' | 'pending' | 'done'
  isPast = false,
  estimatedStart,
  overdue = false,
  conflict = false, // 前のタスクと開始予定時刻が重なっている
  readOnly = false,
  canEditNotes = false, // 名前とメモだけ直せるか（今日と昨日）
  onOpenNote,
  onUpdate,
  onDelete,
  onStart,
  onComplete,
}) {
  /**
   * 見積は「分の数値」で持ち、表示だけ HH:MM にする。
   *   入力中は4桁の数字（0120）を出し、離れると 01:20 に戻す。
   */
  const [estText, setEstText] = useState('');
  const [estFocused, setEstFocused] = useState(false);

  useEffect(() => {
    if (!estFocused) setEstText('');
  }, [task.estimatedMinutes, estFocused]);

  const handleEstFocus = (e) => {
    const el = e.target;
    setEstFocused(true);
    setEstText(minutesToEstDigits(task.estimatedMinutes));
    requestAnimationFrame(() => el.select());
  };

  const commitEst = () => {
    setEstFocused(false);
    // 全角→半角変換
    let value = estText;
    value = value.replace(/[０-９]/g, (s) => 
      String.fromCharCode(s.charCodeAt(0) - 0xFEE0)
    );
    const parsed = estInputToMinutes(value);
    // 無効なとき（4桁の後ろが60以上、5桁以上）は元の値に戻す
    if (parsed === undefined) return;
    if (parsed !== task.estimatedMinutes) onUpdate(task.id, { estimatedMinutes: parsed });
  };

  const actualSeconds = diffSeconds(task.startedAt, task.endedAt);
  const estNum = task.estimatedMinutes;
  const hasEst = estNum !== null && estNum !== undefined;
  const overRun = hasEst && actualSeconds > estNum * 60;

  const rowClass = [
    'task-row',
    mode,
    overdue ? 'overdue' : '',
    conflict ? 'conflict' : '',
    readOnly ? 'locked' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const sizeCls = nameSizeClass(task.name);

  /** 入力欄に出す文字。入力中は打った数字、ふだんは HH:MM */
  const estValue = estFocused ? estText : hasEst ? minutesToEst(estNum) : '';

  const deleteBtn = !readOnly && (
    <button className="btn-delete" onClick={() => onDelete(task.id)} title="削除">
      ×
    </button>
  );

  /** メモ。完了タスクは comment、それ以外は note を使う */
  const noteField = mode === 'done' ? 'comment' : 'note';
  const noteValue = task[noteField] || '';

  /** 画面が狭いときに開く、メモのボタン */
  const noteBtn = (
    <button
      type="button"
      className={`btn-note ${noteValue ? 'has-note' : ''}`}
      onClick={() => onOpenNote && onOpenNote(task)}
      title="メモ"
    >
      ✎
    </button>
  );

  /** 行の中に置くメモ欄。画面が狭いときは隠れる */
  const noteInline = canEditNotes ? (
    <input
      className="row-note"
      type="text"
      value={noteValue}
      placeholder="メモ"
      title={noteValue}
      onChange={(e) => onUpdate(task.id, { [noteField]: e.target.value })}
    />
  ) : (
    <span className="row-note static" title={noteValue}>
      {noteValue}
    </span>
  );

  return (
    <div className={rowClass}>
      {/* 左端のアクション。完了の✓はここ（パソコン）とログ内（スマホ）の両方に置き、
          表示はCSSで切り替える */}
      <div className="row-action">
        {mode === 'running' && (
          // 実行中は日付に関係なく完了できる（0時をまたいだ場合のため）
          <button className="btn-complete" onClick={() => onComplete(task.id)}>
            完了
          </button>
        )}
        {mode === 'pending' && !readOnly && (
          <button 
            className="btn-start" 
            onClick={() => onStart(task.id)}
            disabled={isPast}
            title={isPast ? '過去のタスクは開始できません' : ''}
          >
            開始
          </button>
        )}
        {mode === 'pending' && readOnly && <span className="row-mark">–</span>}
        {mode === 'done' && <span className="row-mark done">✓</span>}
      </div>

      {/* タスク名。昨日までは直せる（記録そのものは直せない） */}
      {!canEditNotes ? (
        <div className={`row-name static ${sizeCls}`} title={task.name}>
          {task.name || '(無題)'}
        </div>
      ) : (
        <input
          className={`row-name ${sizeCls}`}
          type="text"
          value={task.name}
          title={task.name}
          onChange={(e) => onUpdate(task.id, { name: e.target.value })}
          placeholder="タスク名"
        />
      )}

      {/* 実行中：タイマー（ここだけ毎秒更新） */}
      {mode === 'running' && (
        <TimerDisplay startedAt={task.startedAt} estimatedMinutes={task.estimatedMinutes} />
      )}

      {/* 予定：開始予定時刻 + 予想開始 */}
      {mode === 'pending' && (
        <>
          {readOnly ? (
            <div className="row-static-time">
            <span className="num">{task.scheduledStartTime || '--:--'}</span>
          </div>
          ) : (
            <TimeInput
              className="row-time"
              value={task.scheduledStartTime}
              onChange={(v) => onUpdate(task.id, { scheduledStartTime: v })}
              title="開始予定時刻"
            />
          )}
          {estimatedStart && (
            <div className={`row-forecast ${overdue ? 'overdue' : ''}`}>
              <span className="forecast-label">予想</span>
              <span className="forecast-time">{estimatedStart}</span>
            </div>
          )}
        </>
      )}

      {/* 見積。時分で表示する（完了タスクのログだけは分のまま） */}
      {mode !== 'done' &&
        (readOnly ? (
          <div className="row-static-time est">
            <span className="num">{minutesToEst(estNum)}</span>
          </div>
        ) : (
          <input
            className="row-est"
            type="text"
            inputMode="numeric"
            placeholder="--:--"
            maxLength={4}
            value={estValue}
            onChange={(e) => setEstText(e.target.value.replace(/[^0-9]/g, ''))}
            onFocus={handleEstFocus}
            onBlur={commitEst}
            onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
            title="見積時間。1〜3桁は分、4桁は時分（80→01:20 / 0120→01:20）"
          />
        ))}

      {/* 完了：ログ。削除ボタンもこの中に入れて、スマホでは2行目にまとめる */}
      {mode === 'done' && (
        <div className="row-log">
          <span className="log-check">✓</span>
          <span className="log-cell">
            見積 <span className="num">{hasEst ? estNum : '--'}</span>分
          </span>
          <span className={`log-cell actual ${overRun ? 'over' : ''}`} title="実績（時:分:秒）">
            実績 <span className="num">{secToClock(actualSeconds)}</span>
          </span>
          <span className="log-cell wide">
            <span className="num">{isoToHM(task.startedAt)}</span> ~{' '}
            <span className="num">{isoToHM(task.endedAt)}</span>
          </span>

          {/* メモ。画面が狭いときは隠して、✎ を押したときの別画面で書く */}
          {noteInline}
          {noteBtn}
          {deleteBtn}
        </div>
      )}

      {mode !== 'done' && noteInline}
      {mode !== 'done' && noteBtn}
      {mode !== 'done' && deleteBtn}
    </div>
  );
}

export default TaskRow;
